import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { REGIONS } from "@/lib/regions";

export const ROLE_IT = { artist: "Artisti", supplier: "Fornitori", public_entity: "Enti Pubblici", business: "Imprese / P.IVA", citizen: "Privati", admin: "Admin" };
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "-");
export const eur = (n) => (n === null || n === undefined ? "-" : `€${Number(n).toFixed(2)}`);

export const Stat = ({ label, value, testId, hint }) => (
  <div className="card-premium p-5" data-testid={testId}>
    <div className="overline text-slate-500">{label}</div>
    <div className="font-display text-3xl font-bold mt-1 text-navy">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export const Table = ({ cols, rows, empty, testId }) => (
  <div className="card-premium overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-navy text-white"><tr>{cols.map((c) => <th key={c} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-slate-500">{empty}</td></tr>}
        {rows.map((r, i) => <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">{r.map((c, j) => <td key={j} className="px-4 py-3 whitespace-nowrap">{c}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

export const useAdmin = (path, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const load = () => api.get(path, { params }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [path, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
};

export const OverviewSection = () => {
  const [d] = useAdmin("/admin/overview");
  if (!d) return <p className="text-sm text-slate-500">Caricamento…</p>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Iscritti totali" value={d.total} testId="ov-total"/>
        <Stat label="Nuovi oggi" value={d.new.today} testId="ov-today"/>
        <Stat label="Nuovi 7 giorni" value={d.new.week} testId="ov-week"/>
        <Stat label="Nuovi 30 giorni" value={d.new.month} testId="ov-month"/>
        <Stat label="Utenti attivi" value={d.active} hint="accesso negli ultimi 30 giorni" testId="ov-active"/>
        <Stat label="Utenti inattivi" value={d.inactive} testId="ov-inactive"/>
        <Stat label="Profili Premium" value={d.premium} testId="ov-premium"/>
        <Stat label="Sospesi" value={d.suspended} testId="ov-suspended"/>
      </div>
      <div>
        <div className="overline text-gold mb-3">Iscritti per ruolo</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(d.byRole).map(([r, n]) => <Stat key={r} label={ROLE_IT[r] || r} value={n} testId={`ov-role-${r}`}/>)}
        </div>
      </div>
    </div>
  );
};

export const PaymentsSection = () => {
  const [d] = useAdmin("/admin/payments");
  if (!d) return <p className="text-sm text-slate-500">Caricamento…</p>;
  return (
    <div className="space-y-6" data-testid="admin-payments">
      <p className="text-sm text-amber-700">I pagamenti online non sono ancora attivi: incassi e transazioni compariranno qui dopo l'attivazione.</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Incassi totali" value={eur(d.totalRevenue)} testId="pay-total"/>
        <Stat label="Abbonamenti attivi" value={d.subscriptions.active} testId="pay-active"/>
        <Stat label="Scaduti" value={d.subscriptions.expired} testId="pay-expired"/>
        <Stat label="Disdetti" value={d.subscriptions.canceled} testId="pay-canceled"/>
        <Stat label="Premium assegnati da admin" value={d.subscriptions.manual} testId="pay-manual"/>
      </div>
      <Table testId="pay-table" cols={["Data", "Utente", "Email", "Piano", "Importo", "Stato"]} empty="Nessuna transazione"
        rows={d.transactions.map((t) => [fmtDate(t.date), t.user, t.email, t.plan, eur(t.amount), t.status])}/>
    </div>
  );
};

const BOOK_TABS = [["all", "Tutti"], ["signed", "Firmati"], ["pending", "In attesa"], ["completed", "Completati"], ["cancelled", "Annullati"]];
const BOOK_STATUS = { pending_artist: "In attesa", confirmed: "Confermato", rejected: "Rifiutato", expired: "Scaduto" };

export const BookingsSection = () => {
  const [status, setStatus] = useState("all");
  const [d] = useAdmin("/admin/bookings", status === "all" ? {} : { status });
  return (
    <div className="space-y-4" data-testid="admin-bookings">
      <div className="flex flex-wrap gap-2">
        {BOOK_TABS.map(([k, l]) => (
          <button key={k} data-testid={`bk-filter-${k}`} onClick={() => setStatus(k)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${status === k ? "bg-navy text-white border-navy" : "border-slate-300 hover:border-gold"}`}>
            {l}{d && k !== "all" ? ` · ${d.counts[k]}` : ""}
          </button>
        ))}
      </div>
      {d && <Table testId="bk-table" cols={["Committente", "Artista / Fornitore", "Data evento", "Luogo", "Importo", "Stato"]} empty="Nessun contratto"
        rows={d.items.map((b) => [b.client, b.artist, b.eventDate, b.eventLocation, eur(b.amount), BOOK_STATUS[b.status] || b.status])}/>}
    </div>
  );
};

export const AdsSection = () => {
  const [region, setRegion] = useState("all");
  const [d] = useAdmin("/admin/ads", region === "all" ? {} : { region });
  return (
    <div className="space-y-4" data-testid="admin-ads">
      <select data-testid="ads-region" value={region} onChange={(e) => setRegion(e.target.value)} className="border border-slate-300 rounded-full px-4 py-2 text-sm">
        <option value="all">Tutte le zone</option>
        {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      {d && <Table testId="ads-table" cols={["Inserzionista", "Zona", "Stato", "Visualizzazioni", "Click", "CTR", "Durata", "Totale"]} empty="Nessuna campagna"
        rows={d.map((a) => [a.advertiser, `${a.city ? a.city + ", " : ""}${a.region || "-"}`, a.status === "approved" ? "Attiva" : "In attesa",
          a.views, a.clicks, a.views ? `${((a.clicks / a.views) * 100).toFixed(1)}%` : "-", `${a.days} gg`, eur(a.totalGross)])}/>}
    </div>
  );
};

export const AnalyticsSection = () => {
  const [days, setDays] = useState(30);
  const [d] = useAdmin("/admin/analytics", { days });
  const List = ({ title, rows, testId }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return (
      <div className="card-premium p-5" data-testid={testId}>
        <div className="overline text-gold mb-3">{title}</div>
        {rows.length === 0 && <p className="text-sm text-slate-500">Nessun dato</p>}
        <div className="space-y-2">{rows.map((r) => (
          <div key={r.label} className="text-sm">
            <div className="flex justify-between gap-2"><span className="truncate">{r.label}</span><span className="font-semibold">{r.count}</span></div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-full bg-gold rounded-full" style={{ width: `${(r.count / max) * 100}%` }}/></div>
          </div>))}
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-4" data-testid="admin-analytics">
      <select data-testid="an-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded-full px-4 py-2 text-sm">
        {[7, 30, 90, 365].map((n) => <option key={n} value={n}>Ultimi {n} giorni</option>)}
      </select>
      {d && (
        <>
          <div className="grid grid-cols-2 gap-4"><Stat label="Pagine viste" value={d.totalViews} testId="an-views"/><Stat label="Visitatori unici" value={d.visitors} testId="an-visitors"/></div>
          <div className="grid lg:grid-cols-3 gap-4">
            <List title="Pagine più visitate" rows={d.pages} testId="an-pages"/>
            <List title="Provenienza traffico" rows={d.sources} testId="an-sources"/>
            <List title="Dispositivi" rows={d.devices} testId="an-devices"/>
          </div>
        </>
      )}
    </div>
  );
};
